import { FC } from 'react';

import { Controller, useFieldArray, useFormContext } from 'react-hook-form-mui';
import { ArrowIcon, DeleteIcon } from 'assets';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import { Button, Input, Select } from 'view/components';
import * as S from 'view/modals/OnboardingWizard/styled';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import TestSelect from 'view/pages/profile/TestPage/Select';

interface WorkExperienceStepProps {
  onNext: () => void;
  onBack: () => void;
  isSaving?: boolean;
}

const months = [
  { value: 'January', label: 'January' },
  { value: 'February', label: 'February' },
  { value: 'March', label: 'March' },
  { value: 'April', label: 'April' },
  { value: 'May', label: 'May' },
  { value: 'June', label: 'June' },
  { value: 'July', label: 'July' },
  { value: 'August', label: 'August' },
  { value: 'September', label: 'September' },
  { value: 'October', label: 'October' },
  { value: 'November', label: 'November' },
  { value: 'December', label: 'December' },
];

const currentYear = new Date().getFullYear();
const currentMonthIndex = new Date().getMonth();

const years = Array.from({ length: 50 }, (_, i) => {
  return {
    value: String(currentYear - i),
    label: String(currentYear - i),
  };
});

const getAvailableMonths = (selectedYear: string) => {
  if (selectedYear === String(currentYear)) {
    return months.slice(0, currentMonthIndex + 1);
  }
  return months;
};

const getAvailableEndMonths = (startMonth: string, startYear: string, endYear: string) => {
  const startIndex = months.findIndex((m) => {
    return m.value === startMonth;
  });
  if (startYear === endYear && endYear === String(currentYear)) {
    return months.slice(startIndex, currentMonthIndex + 1);
  }
  if (startYear === endYear) {
    return months.slice(startIndex);
  }
  return months;
};

const WorkExperienceStep: FC<WorkExperienceStepProps> = ({ onNext, onBack, isSaving = false }) => {
  const { watch, setValue, control, register } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'workExperience',
  });

  const { data: positionTypeData, isLoading: isPositionTypeLoading } = useGetDropdownData(
    DropdownsEnum.POSITION_TYPE
  );

  const addPosition = () => {
    append({
      id: null,
      companyName: '',
      jobTitle: '',
      positionType: null,
      startDateMonth: '',
      startDateYear: '',
      endDateMonth: '',
      endDateYear: '',
      isCurrentCompany: false,
      responsibilities: '',
    });
  };

  return (
    <S.WizardContent>
      <S.StepHeader>
        <S.StepTitle>Work Experience</S.StepTitle>
        <S.StepSubtitle>
          Add your work history to help employers understand your background and experience
        </S.StepSubtitle>
      </S.StepHeader>
      <S.StepFormContainerTight>
        {fields.map((field, index) => {
          const isCurrentCompany = watch(`workExperience.${index}.isCurrentCompany`) || false;
          const startDateMonth = watch(`workExperience.${index}.startDateMonth`);
          const startDateYear = watch(`workExperience.${index}.startDateYear`);
          const endDateMonth = watch(`workExperience.${index}.endDateMonth`);
          const endDateYear = watch(`workExperience.${index}.endDateYear`);

          return (
            <S.PositionContainer key={field.id}>
              <S.PositionHeader>
                <S.PositionTitle>Position {index + 1}</S.PositionTitle>
                {fields.length > 1 && (
                  <S.DeleteButton
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    <DeleteIcon />
                    <span>Delete</span>
                  </S.DeleteButton>
                )}
              </S.PositionHeader>

              <Input
                name={`workExperience.${index}.companyName`}
                label="Company Name"
                placeholder="Enter company name"
                marginbottom={12}
              />
              <Input
                name={`workExperience.${index}.jobTitle`}
                label="Job Title / Role"
                placeholder="Enter job title / role"
                marginbottom={12}
              />
              <Controller
                name={`workExperience.${index}.positionType`}
                control={control}
                render={({ field: posField }) => {
                  return (
                    <TestSelect
                      {...posField}
                      label="Position Type"
                      options={positionTypeData || []}
                      selectedValue={posField.value}
                      onChange={(selected: unknown) => {
                        posField.onChange(selected);
                      }}
                      disabled={isPositionTypeLoading || !positionTypeData}
                    />
                  );
                }}
              />

              <S.DateContainer>
                <S.DateLabel>Start Date</S.DateLabel>
                <S.DateSelectWrapper>
                  <Select
                    name={`workExperience.${index}.startDateMonth`}
                    options={getAvailableMonths(startDateYear)}
                    selectedValue={startDateMonth || ''}
                    onChange={(value: string) => {
                      setValue(`workExperience.${index}.startDateMonth`, value);
                      setValue(`workExperience.${index}.endDateMonth`, '');
                      setValue(`workExperience.${index}.endDateYear`, '');
                    }}
                    register={register}
                    disabled={!startDateYear}
                  />
                  <Select
                    name={`workExperience.${index}.startDateYear`}
                    options={years}
                    selectedValue={startDateYear || ''}
                    onChange={(value: string) => {
                      setValue(`workExperience.${index}.startDateYear`, value);
                      setValue(`workExperience.${index}.startDateMonth`, '');
                      setValue(`workExperience.${index}.endDateMonth`, '');
                      setValue(`workExperience.${index}.endDateYear`, '');
                    }}
                    register={register}
                  />
                </S.DateSelectWrapper>
              </S.DateContainer>

              <S.CheckboxWithText style={{ marginBottom: '12px' }}>
                <S.CustomCheckbox
                  checked={isCurrentCompany}
                  onChange={(e) => {
                    setValue(`workExperience.${index}.isCurrentCompany`, e.target.checked);
                    if (e.target.checked) {
                      setValue(`workExperience.${index}.endDateMonth`, '');
                      setValue(`workExperience.${index}.endDateYear`, '');
                    }
                  }}
                />
                <p>I am currently working here</p>
              </S.CheckboxWithText>

              {!isCurrentCompany && (
                <S.DateContainer>
                  <S.DateLabel>End Date</S.DateLabel>
                  <S.DateSelectWrapper>
                    <Select
                      name={`workExperience.${index}.endDateMonth`}
                      options={getAvailableEndMonths(startDateMonth, startDateYear, endDateYear)}
                      selectedValue={endDateMonth || ''}
                      onChange={(value: string) => {
                        setValue(`workExperience.${index}.endDateMonth`, value);
                      }}
                      disabled={!startDateMonth || !startDateYear || !endDateYear}
                      register={register}
                    />
                    <Select
                      name={`workExperience.${index}.endDateYear`}
                      options={years.filter((y) => {
                        return Number(y.value) >= Number(startDateYear);
                      })}
                      selectedValue={endDateYear || ''}
                      onChange={(value: string) => {
                        setValue(`workExperience.${index}.endDateYear`, value);
                        setValue(`workExperience.${index}.endDateMonth`, '');
                      }}
                      register={register}
                      disabled={!startDateYear}
                    />
                  </S.DateSelectWrapper>
                </S.DateContainer>
              )}

              <Input
                name={`workExperience.${index}.responsibilities`}
                label="Responsibilities (optional)"
                placeholder="Enter your responsibilities..."
                multiline
                rows={3}
                marginbottom={0}
              />
            </S.PositionContainer>
          );
        })}

        <S.AddPositionButton>
          <Button variant="outlined" text="Add Position" onClick={addPosition} />
        </S.AddPositionButton>
      </S.StepFormContainerTight>
      <S.WizardFooter>
        <Button variant="outlined" text="Back" onClick={onBack} disabled={isSaving} />
        <Button
          variant="contained"
          text={isSaving ? 'Saving...' : 'Next'}
          onClick={onNext}
          disabled={isSaving}
          endIcon={<ArrowIcon rotate="right" color="#FFFFFF" />}
        />
      </S.WizardFooter>
    </S.WizardContent>
  );
};

export default WorkExperienceStep;
